/**
 * Side-by-side refinance choices for a home the borrower owns: keep the
 * current loan, pay extra on it, or refinance at par / with discount points /
 * on a shorter term.
 *
 * Options are ranked by what they cost over the years the borrower expects to
 * keep the loan: upfront costs + every payment made + the balance still owed
 * at the end (− any cash taken out). Comparing monthly payments alone favors
 * restarting a 30-year clock, which usually costs more in total.
 */
import { monthlyPrincipalAndInterest } from '@/utils/calculator/profileDti';

/** Rule-of-thumb rate reduction bought by one discount point (1% of the loan). */
export const RATE_DROP_PER_POINT = 0.25;

const MAX_MONTHS = 600;

export type RefinanceOptionId =
  | 'keep'
  | 'keep_extra'
  | 'refi'
  | 'refi_1pt'
  | 'refi_2pt'
  | 'refi_15'
  | 'refi_keep_payment';

export interface CurrentLoanInput {
  balance: number;
  ratePct: number;
  /** Principal + interest only. Derived from the remaining term when missing. */
  monthlyPi?: number;
  remainingYears?: number;
}

export interface NewLoanQuote {
  ratePct: number;
  termYears: number;
  loanAmount: number;
  /** Lender, title, government and prepaid costs (no discount points). */
  closingCosts: number;
  /** Mortgage insurance / guarantee fee per month, if any. */
  monthlyMi?: number;
}

export interface RefinanceOptionsInput {
  current: CurrentLoanInput;
  /** The par-rate quote for the borrower's chosen term. */
  quote: NewLoanQuote;
  /** A 15-year quote; omitted when the chosen term is already 15 years. */
  quote15?: NewLoanQuote;
  horizonYears: number;
  /** Extra principal per month for the "pay more" options. */
  extraPrincipal: number;
}

export interface RefinanceOption {
  id: RefinanceOptionId;
  label: string;
  detail: string;
  ratePct: number;
  /** Required payment: principal + interest + mortgage insurance. */
  requiredPayment: number;
  /** What the borrower actually pays each month (required + any extra). */
  monthlyPayment: number;
  /** Change in the monthly outlay vs the current loan (+ = pays more). */
  monthlyChange: number;
  upfrontCosts: number;
  pointsCost: number;
  cashOut: number;
  payoffMonths: number;
  interestToPayoff: number;
  /** Months until this option has cost less overall than keeping the loan. */
  breakEvenMonths: number | null;
  horizonCost: number;
  /** Positive = cheaper than keeping the current loan over the horizon. */
  savingsVsKeep: number;
}

export interface RefinanceOptionsResult {
  currentPi: number;
  currentRemainingMonths: number;
  options: RefinanceOption[];
  /** Cheapest option over the horizon. */
  bestId: RefinanceOptionId;
}

/** Months to pay `balance` off at `payment`; null if it never amortizes. */
export function monthsToPayoff(
  balance: number,
  ratePct: number,
  payment: number
): number | null {
  if (balance <= 0) return 0;
  const r = ratePct / 100 / 12;
  if (r <= 0) return payment > 0 ? Math.ceil(balance / payment) : null;
  if (payment <= balance * r) return null;
  return Math.ceil(-Math.log(1 - (r * balance) / payment) / Math.log(1 + r));
}

type Schedule = {
  /** Cumulative cash paid after month m (index m, m=0 is nothing paid). */
  paid: number[];
  /** Balance owed after month m. */
  balance: number[];
  interest: number;
  payoffMonths: number;
};

function schedule(
  principal: number,
  ratePct: number,
  payment: number,
  monthlyMi: number,
  months: number
): Schedule {
  const r = ratePct / 100 / 12;
  const paid = [0];
  const balance = [principal];
  let bal = principal;
  let total = 0;
  let interest = 0;
  let payoffMonths = months;
  for (let m = 1; m <= months; m += 1) {
    if (bal > 0.005) {
      const i = bal * r;
      const toPrincipal = Math.min(bal, payment - i);
      interest += i;
      total += i + toPrincipal + monthlyMi;
      bal -= toPrincipal;
      if (bal <= 0.005) {
        bal = 0;
        payoffMonths = m;
      }
    }
    paid.push(total);
    balance.push(bal);
  }
  return { paid, balance, interest, payoffMonths };
}

function costAt(
  s: Schedule,
  month: number,
  upfront: number,
  cashOut: number
): number {
  const m = Math.min(month, s.paid.length - 1);
  return upfront + s.paid[m] + s.balance[m] - cashOut;
}

export function buildRefinanceOptions(
  input: RefinanceOptionsInput
): RefinanceOptionsResult | null {
  const { current, quote, quote15, extraPrincipal } = input;
  if (!(current.balance > 0) || !(current.ratePct > 0)) return null;

  const givenMonths =
    current.remainingYears && current.remainingYears > 0
      ? Math.round(current.remainingYears * 12)
      : undefined;
  const currentPi =
    current.monthlyPi && current.monthlyPi > 0
      ? current.monthlyPi
      : givenMonths
      ? monthlyPrincipalAndInterest(
          current.balance,
          current.ratePct,
          givenMonths / 12
        )
      : 0;
  if (!(currentPi > 0)) return null;
  const currentRemainingMonths =
    monthsToPayoff(current.balance, current.ratePct, currentPi) ??
    givenMonths ??
    MAX_MONTHS;

  const horizonMonths = Math.max(1, Math.round(input.horizonYears * 12));
  const extra = Math.max(0, extraPrincipal || 0);

  type Spec = {
    id: RefinanceOptionId;
    label: string;
    detail: string;
    principal: number;
    ratePct: number;
    requiredPi: number;
    monthlyMi: number;
    extra: number;
    closingCosts: number;
    pointsCost: number;
    cashOut: number;
  };

  const specs: Spec[] = [
    {
      id: 'keep',
      label: 'Keep current loan',
      detail: 'No closing costs; pay as scheduled',
      principal: current.balance,
      ratePct: current.ratePct,
      requiredPi: currentPi,
      monthlyMi: 0,
      extra: 0,
      closingCosts: 0,
      pointsCost: 0,
      cashOut: 0,
    },
  ];
  if (extra > 0) {
    specs.push({
      id: 'keep_extra',
      label: `Keep loan, pay $${Math.round(extra).toLocaleString()} more`,
      detail: 'Extra principal each month; no closing costs',
      principal: current.balance,
      ratePct: current.ratePct,
      requiredPi: currentPi,
      monthlyMi: 0,
      extra,
      closingCosts: 0,
      pointsCost: 0,
      cashOut: 0,
    });
  }

  const refiSpec = (
    q: NewLoanQuote,
    id: RefinanceOptionId,
    label: string,
    detail: string,
    points = 0,
    payExtra = 0
  ): Spec => {
    const ratePct = Math.max(0.01, q.ratePct - points * RATE_DROP_PER_POINT);
    return {
      id,
      label,
      detail,
      principal: q.loanAmount,
      ratePct,
      requiredPi: monthlyPrincipalAndInterest(
        q.loanAmount,
        ratePct,
        q.termYears
      ),
      monthlyMi: q.monthlyMi || 0,
      extra: payExtra,
      closingCosts: q.closingCosts,
      pointsCost: (points / 100) * q.loanAmount,
      cashOut: Math.max(0, q.loanAmount - current.balance),
    };
  };

  const par = refiSpec(
    quote,
    'refi',
    `Refinance ${quote.termYears}-yr`,
    'Market rate, no points'
  );
  specs.push(
    par,
    refiSpec(
      quote,
      'refi_1pt',
      `${quote.termYears}-yr, buy down 1 point`,
      `Pay 1% of the loan for ~${RATE_DROP_PER_POINT}% off the rate`,
      1
    ),
    refiSpec(
      quote,
      'refi_2pt',
      `${quote.termYears}-yr, buy down 2 points`,
      `Pay 2% of the loan for ~${2 * RATE_DROP_PER_POINT}% off the rate`,
      2
    )
  );
  const parRequired = par.requiredPi + par.monthlyMi;
  if (currentPi > parRequired + 1) {
    specs.push(
      refiSpec(
        quote,
        'refi_keep_payment',
        `${quote.termYears}-yr, keep paying today's amount`,
        'Refinance, then put the monthly savings toward principal',
        0,
        currentPi - parRequired
      )
    );
  }
  if (quote15) {
    specs.push(
      refiSpec(
        quote15,
        'refi_15',
        'Refinance 15-yr',
        'Higher payment, far less interest'
      )
    );
  }

  const keepSchedule = schedule(
    current.balance,
    current.ratePct,
    currentPi,
    0,
    MAX_MONTHS
  );

  const options = specs.map((s): RefinanceOption => {
    const payment = s.requiredPi + s.extra;
    const sched = schedule(
      s.principal,
      s.ratePct,
      payment,
      s.monthlyMi,
      MAX_MONTHS
    );
    const upfront = s.closingCosts + s.pointsCost;
    let breakEvenMonths: number | null = null;
    if (s.id !== 'keep') {
      for (let m = 1; m <= MAX_MONTHS; m += 1) {
        if (
          costAt(sched, m, upfront, s.cashOut) <= costAt(keepSchedule, m, 0, 0)
        ) {
          breakEvenMonths = m;
          break;
        }
      }
    }
    const horizonCost = costAt(sched, horizonMonths, upfront, s.cashOut);
    return {
      id: s.id,
      label: s.label,
      detail: s.detail,
      ratePct: s.ratePct,
      requiredPayment: s.requiredPi + s.monthlyMi,
      monthlyPayment: payment + s.monthlyMi,
      monthlyChange: payment + s.monthlyMi - currentPi,
      upfrontCosts: upfront,
      pointsCost: s.pointsCost,
      cashOut: s.cashOut,
      payoffMonths: sched.payoffMonths,
      interestToPayoff: sched.interest,
      breakEvenMonths,
      horizonCost,
      savingsVsKeep: 0,
    };
  });

  const keepCost = options[0].horizonCost;
  let best = options[0];
  for (const o of options) {
    o.savingsVsKeep = keepCost - o.horizonCost;
    if (o.horizonCost < best.horizonCost) best = o;
  }

  return { currentPi, currentRemainingMonths, options, bestId: best.id };
}

export type DtiStatus = 'strong' | 'ok' | 'stretch' | 'over';

/** Rough read of a back-end DTI against agency guidelines. */
export function dtiStatus(dti: number): DtiStatus {
  if (dti <= 36) return 'strong';
  if (dti <= 45) return 'ok';
  if (dti <= 50) return 'stretch';
  return 'over';
}

/** Largest total housing payment that keeps back-end DTI at `maxDti`. */
export function maxHousingPayment(
  monthlyIncome: number,
  monthlyDebt: number,
  maxDti = 45
): number {
  return Math.max(0, monthlyIncome * (maxDti / 100) - Math.max(0, monthlyDebt));
}
